/**
 * Data hook for fetching chat details
 * Separates GraphQL operations from UI components
 */

import type { ChatDetailsResponse, EditChatConfig } from '@choco-admin/types';
import { useCallback, useEffect, useState, useRef } from 'react';

import { useNotification } from '../../../contexts/NotificationContext';
import { useGetChatDataQuery } from '../../../generated';
import { CHAT_USERS_PAGE_SIZE } from '../constants/chatConstants';
import { mapToChatDetailsResponse } from '../utils';

export interface UseGetChatDataResponse {
  chatData: ChatDetailsResponse | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export const shouldShowInitialChatLoading = (
  loading: boolean,
  chatData: ChatDetailsResponse | null
): boolean => loading && !chatData;

export const useGetChatData = (
  chatId: string,
  config: EditChatConfig = {}
): UseGetChatDataResponse => {
  const { onError, captureException } = config;
  const [chatData, setChatData] = useState<ChatDetailsResponse | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const { addNotification } = useNotification();

  // Use refs to store callback functions to avoid dependency issues
  const onErrorRef = useRef(onError);
  const captureExceptionRef = useRef(captureException);

  // Update refs when callbacks change
  useEffect(() => {
    onErrorRef.current = onError;
    captureExceptionRef.current = captureException;
  }, [onError, captureException]);

  const {
    data,
    loading,
    error: gqlError,
    refetch: gqlRefetch,
  } = useGetChatDataQuery({
    variables: {
      chatId,
      first: CHAT_USERS_PAGE_SIZE,
      withAllUserFields: true,
      withProducts: true,
    },
    // Never fire the query with an empty chatId (e.g. the create-chat page
    // mounts useDuplicateChatData without a ?duplicateFrom param).
    skip: !chatId,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
  });

  // Map GraphQL data to domain types
  useEffect(() => {
    if (data?.chat) {
      try {
        const mappedData = mapToChatDetailsResponse(
          data.chat,
          data.getSubCatalogsForChat
        );
        setChatData(mappedData);
        setError(null);
      } catch (mappingError) {
        const err = new Error(`Failed to map chat data: ${mappingError}`);
        setError(err);
        onErrorRef.current?.(err);
        captureExceptionRef.current?.(err);
      }
    }
  }, [data]);

  // Handle GraphQL errors — with errorPolicy:'all', partial field errors arrive here
  // even when the primary data (data.chat) loaded successfully.
  // Only treat it as a fatal load failure when chat itself is absent.
  useEffect(() => {
    if (gqlError) {
      if (!data?.chat) {
        // The chat root field failed — surface the error to the user
        const err = new Error(`Failed to fetch chat data: ${gqlError.message}`);
        setError(err);
        onErrorRef.current?.(err);
        captureExceptionRef.current?.(err);

        addNotification({
          title: 'Error Loading Chat',
          message: 'Failed to load chat details. Please try again.',
          type: 'error',
        });
      } else {
        // Field-level partial errors (e.g. appliedMinimumOrderValue auth failure)
        // Chat loaded successfully; log the partial errors for observability only.
        // eslint-disable-next-line no-console
        console.warn(
          '[useGetChatData] Partial field errors (chat data loaded):',
          gqlError.graphQLErrors
        );
      }
    }
  }, [gqlError, data?.chat, addNotification]);

  const refetch = useCallback(async () => {
    try {
      setError(null);
      await gqlRefetch();
    } catch (refetchError) {
      const err = new Error(`Failed to refetch chat data: ${refetchError}`);
      setError(err);
      onErrorRef.current?.(err);
      captureExceptionRef.current?.(err);
    }
  }, [gqlRefetch]);

  return {
    chatData,
    // Only surface the full-page skeleton on the initial load. Background
    // refetches (e.g. the ones triggered after a user add/remove) keep
    // `loading` true via `notifyOnNetworkStatusChange`; if that unmounted the
    // page it would tear down ChatPageContextProvider mid-operation and drop
    // the optimistic write, so the change only appeared after a manual reload.
    isLoading: shouldShowInitialChatLoading(loading, chatData),
    error,
    refetch,
  };
};
